import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { HelpOutline as HelpOutlineIcon, Close as CloseIcon } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { Subject } from 'rxjs';

import { openHelper } from '/common/Helper';
import type { IRouteProps } from '/Router/models';
import { uiToolkit } from '/store/toolkits';

import { HelperIconButton, IconWrapper, FABWrapper, PulseRing, NudgeToast, NudgeIcon, NudgeDismiss } from './styles';

const NUDGE_KEY = 'help_nudge_seen';
const SHOW_DELAY_MS = 800;
const AUTO_DISMISS_MS = 6000;
const EXIT_DURATION_MS = 300;

const nudgeTriggerSubject = new Subject<void>();

export const triggerHintNudge = () => nudgeTriggerSubject.next();

export function HintEnhancer({ children, helperFile }: IRouteProps) {
  const isHint = useSelector(uiToolkit.selectors.isHints);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
  }, []);

  const dismissNudge = useCallback(() => {
    clearTimers();
    localStorage.setItem(NUDGE_KEY, 'true');
    setExiting(true);
    exitTimerRef.current = setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, EXIT_DURATION_MS);
  }, [clearTimers]);

  const handleOpenHelper = useCallback(() => {
    dismissNudge();
    openHelper({ path: helperFile });
  }, [dismissNudge, helperFile]);

  useEffect(() => {
    if (!isHint || localStorage.getItem(NUDGE_KEY)) return;

    showTimerRef.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    autoTimerRef.current = setTimeout(dismissNudge, SHOW_DELAY_MS + AUTO_DISMISS_MS);

    return () => {
      clearTimers();
      // TODO: remove when all docs will be changed for current functionality. Now it's displayed one time per app session
      localStorage.setItem(NUDGE_KEY, 'true');
    };
  }, [isHint, dismissNudge, clearTimers]);

  useEffect(() => {
    const subscription = nudgeTriggerSubject.subscribe(() => {
      if (!isHint) return;

      clearTimers();
      setExiting(false);
      setVisible(true);
      autoTimerRef.current = setTimeout(dismissNudge, AUTO_DISMISS_MS);
    });

    return () => {
      clearTimers();
      subscription.unsubscribe();
    };
  }, [isHint, dismissNudge, clearTimers]);

  if (!isHint) {
    return <>{children}</>;
  }

  return (
    <>
      <IconWrapper>
        <FABWrapper>
          {visible && !exiting && <PulseRing aria-hidden='true' />}
          <HelperIconButton color='primary' onClick={handleOpenHelper} size='large'>
            <HelpOutlineIcon />
          </HelperIconButton>
        </FABWrapper>
      </IconWrapper>
      {visible && (
        <NudgeToast role='status' aria-live='polite' data-exiting={exiting ? 'true' : undefined}>
          <NudgeIcon aria-hidden='true'>?</NudgeIcon>
          <Typography variant='body2'>Need help?</Typography>
          <NudgeDismiss size='small' onClick={dismissNudge} aria-label='Dismiss help nudge'>
            <CloseIcon fontSize='small' />
          </NudgeDismiss>
        </NudgeToast>
      )}
      {children}
    </>
  );
}

HintEnhancer.canEnhanced = (props: IRouteProps): boolean => !!props.helperFile;
